import Cookies from "./cookies";
import Events from "./events";
import Html from "./html";

class Weather {
    constructor(
        private html: Html,
        private cookies: Cookies,
        private events: Events
    ) { }

    /**
     * By default we write Ljubljana's weather info into
     * 24ur.com's top menu. If user has selected any other
     * city as his prefered weather city, let's fetch info
     * for that city and replace HTML in menu.
     */
    showCorrectWeather(): void {
        const city = this.cookies.get('weatherCity') || 'ljubljana';
        this.selectCity(city, null, false);

        this.fetchWeather('.weather_city_data', city);
    }

    /**
     * Opens or closes popup for selecting weather city.
     * We don't have any HTML for this popup initially, we have to load
     * it the first time user wants to see it.
     */
    toggleCityPopup(selector: string, parent: HTMLElement|null = null): void {

        let weatherPopup: HTMLElement | null = null;

        if (selector) {
            weatherPopup = this.html.q(selector);
        } else if (parent) {
            weatherPopup = this.html.q('.weather_menu_popup', parent);
        }

        if (!weatherPopup) {
            return;
        }

        this.html.toggleClass(weatherPopup, 'hidden');

        const selectedCity = this.cookies.get('weatherCity') || 'ljubljana';

        // Load HTML from server if this is the first time we show this popup
        const isEmpty = !this.html.q(weatherPopup)?.innerHTML;
        if (isEmpty) {
            this.html.fetchText('/weather/city_popup')
                .then(html => {
                    this.html.writeHTML(weatherPopup, html);
                    this.updateUserCityName();

                    // HTML that is received from XHR ignores all onclick handlers. We
                    // have to manually add them.
                    this.html.qAll('.weather-city-name', weatherPopup)
                        .forEach(elt => {
                            const city = elt.dataset['city'] || '';
                            if (city === selectedCity) {
                                elt.classList.add('active');
                            }
                            elt.addEventListener('click', () => this.selectCity(city, selector));
                        });
                });
        }
    }

    loadCityMain(): void {
        let weatherCity = this.html.q('#weather-city');
        if (!weatherCity) {
            return;
        }

        const selectedCity = this.cookies.get('weatherCity') || 'ljubljana';
        this.html.fetchText('/weather/' + selectedCity + '/page')
            .then(html => {
                this.html.writeHTML(weatherCity, html);

                let weatherPopup = this.html.q('#weather-selector-main');
                if (!weatherPopup) {
                    return;
                }
                
                weatherPopup.addEventListener('click', () => this.toggleCityPopup('', weatherPopup));

                const now = new Date();
                now.setMinutes(0, 0, 0);

                let currentHourTimestamp = Math.floor(now.getTime() / 1000);

                this.html.qAll('.weather-hour-slot').forEach((element: HTMLElement) => {
                    const timestamp = element.dataset['timestamp'];
                   
                    if (timestamp && parseInt(timestamp, 10) < currentHourTimestamp) {
                        element.classList.add('weather-past');
                    }
                });

                const dayElements = this.html.qAll('[class="weather-tab-content"]');
                for (const dayElement of dayElements) {
                    const weatherSlots = this.html.qAll('.weather-hour-slot', dayElement as HTMLElement);
                    
                    if (weatherSlots.length == 0) {
                        continue;
                    }

                    if(Array.from(weatherSlots).every((slot: HTMLElement) => 
                        slot.classList.contains('weather-past'))){
                        continue;
                    };
                    
                    const radioInputId = dayElement.dataset['content'];
                    if (radioInputId) {
                        const radioInput = this.html.q(`#${radioInputId}`) as HTMLInputElement;
                        if (radioInput) {
                            radioInput.checked = true;
                            
                        
                            const firstActiveSlot = Array.from(weatherSlots).find((slot: HTMLElement) => 
                                !slot.classList.contains('weather-past')
                            ) as HTMLElement;
                            
                            if (firstActiveSlot) {
                                firstActiveSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                            }
                        }
                    }
                    break;
                }
                
            });
    }

    fetchWeather(selector: string, city: string = '', template = 'front_box'): Promise<any> {
        if (!city) {
            city = this.cookies.get('weatherCity') || 'ljubljana';
        }

        // Because of old angular site - we saved weather city as Žiri, not as keyword "ziri"
        const hasWrongChar = !!['%', ' ', 'š', 'č', 'ž', 'Š', 'Č', 'Ž'].find(c => city.includes(c));
        if (hasWrongChar) {
            city = 'ljubljana';
        }

        // Hack: don't load weather icon for ljubljana because ljubljana's
        // weather is already in HTML when we create it.
        if (city.toLowerCase() === 'ljubljana') {
            const currentlyDisplayed = this.html.getData('.weather_top', 'city')
            if (currentlyDisplayed === 'ljubljana') {
                return Promise.resolve();
            }
        }

        return this.html.fetchText('/weather/' + city + '/' + template)
            .then(html => {
                this.html.setData('.weather_top', 'city', city);
                this.html.writeHTML(selector, html);
            });
    }

    // This updates name of user's weather city in
    // the menu weather popup and on weather page.
    // City name is send in data-cityname="Ljubljana" html data attribute.
    updateUserCityName(): void {
        const cityName = this.html.getData('#menu_weather_icon', 'cityname')
        if (cityName) {
            this.html.writeHTML('.user_weather_cityname', cityName);
        }
    }

    selectCity(citySlug: string, citiesSelector: string | null = null, sendEvent = true): void {
        this.cookies.setIfImportantAllowed('weatherCity', citySlug);
        if (citiesSelector) {
            this.html.addClass(citiesSelector, 'hidden');
        }
        if (sendEvent) {
            this.events.sendEvent('weather-city-selected', citySlug);
        }
    }
}

export default Weather;